import { z } from 'zod';
import { UuidSchema } from './common.js';

// -----------------------------------------------------------------------------
// Enums
// -----------------------------------------------------------------------------

export const ForecastStatusSchema = z.enum([
  'queued',
  'loading_history',
  'baselining',
  'modeling_impact',
  'scenarios',
  'composing',
  'completed',
  'failed',
  'cancelled',
]);
export type ForecastStatus = z.infer<typeof ForecastStatusSchema>;

export const ForecastMetricSchema = z.enum([
  'organic_clicks',
  'impressions',
  'rankings',
  'conversions',
]);
export type ForecastMetric = z.infer<typeof ForecastMetricSchema>;

export const ForecastScenarioNameSchema = z.enum([
  'do_nothing',
  'execute_current_plan',
  'accelerated',
]);
export type ForecastScenarioName = z.infer<typeof ForecastScenarioNameSchema>;

// -----------------------------------------------------------------------------
// Rows
// -----------------------------------------------------------------------------

export const ForecastSchema = z.object({
  id: UuidSchema,
  tenant_id: UuidSchema,
  project_id: UuidSchema,
  status: ForecastStatusSchema,
  workflow_id: z.string().nullable(),
  horizon_days: z.number().int().min(7).max(365),
  campaign_id: UuidSchema.nullable(),
  calendar_id: UuidSchema.nullable(),
  baseline: z.unknown().nullable(),
  assumptions: z.unknown().nullable(),
  narrative_md: z.string().nullable(),
  warnings: z.array(z.unknown()).default([]),
  started_at: z.string().datetime({ offset: true }).nullable(),
  completed_at: z.string().datetime({ offset: true }).nullable(),
  failed_at: z.string().datetime({ offset: true }).nullable(),
  failure_reason: z.string().nullable(),
  created_at: z.string().datetime({ offset: true }),
});
export type Forecast = z.infer<typeof ForecastSchema>;

export const ForecastScenarioRowSchema = z.object({
  id: UuidSchema,
  tenant_id: UuidSchema,
  forecast_id: UuidSchema,
  name: ForecastScenarioNameSchema,
  label: z.string(),
  horizon_value: z.number(),
  horizon_low: z.number(),
  horizon_high: z.number(),
  uplift_fraction: z.number(),
  points: z.array(
    z.object({
      day_offset: z.number().int(),
      value: z.number(),
      low: z.number(),
      high: z.number(),
    }),
  ),
  rationale: z.string().nullable(),
  created_at: z.string().datetime({ offset: true }),
});
export type ForecastScenarioRow = z.infer<typeof ForecastScenarioRowSchema>;

export const ForecastSensitivitySchema = z.object({
  id: UuidSchema,
  tenant_id: UuidSchema,
  forecast_id: UuidSchema,
  action_label: z.string(),
  contribution_fraction: z.number(),
  rationale: z.string().nullable(),
  created_at: z.string().datetime({ offset: true }),
});
export type ForecastSensitivity = z.infer<typeof ForecastSensitivitySchema>;

// -----------------------------------------------------------------------------
// Requests
// -----------------------------------------------------------------------------

export const CreateForecastRequestSchema = z.object({
  project_id: UuidSchema,
  horizon_days: z.number().int().min(7).max(365).default(90),
  /** When provided, the "execute_current_plan" scenario uses this campaign's steps as the action set. */
  campaign_id: UuidSchema.optional(),
  /** When provided, action impacts are pulled from this calendar's pillar plan. */
  calendar_id: UuidSchema.optional(),
  metric: ForecastMetricSchema.default('organic_clicks'),
  /** Synthetic seed values when no history exists — useful for demos / new projects. */
  synthetic_baseline: z
    .object({
      daily_value: z.number().nonnegative(),
      trend_per_day: z.number().default(0),
    })
    .optional(),
});
export type CreateForecastRequest = z.infer<typeof CreateForecastRequestSchema>;

// -----------------------------------------------------------------------------
// LLM output
// -----------------------------------------------------------------------------

export const ForecastNarrativeOutputSchema = z.object({
  summary: z.string().min(20),
  assumptions: z.array(z.string()).min(1).max(8),
  risks: z.array(z.string()).max(6).default([]),
  recommendations: z.array(z.string()).min(1).max(6),
});
export type ForecastNarrativeOutput = z.infer<typeof ForecastNarrativeOutputSchema>;
